import { NumberField } from "@kobalte/core/number-field";
import { A, useLocation, useNavigate } from "@solidjs/router";
import { stAnalytics } from "@storytell/analytics";
import { useBillingClient, useCallOptions, withBearerToken } from "@storytell/cp-client";
import type { CreateCheckoutRequest } from "@storytell/cp-client/src/gen/billing/v3/billing_service_pb";
import { StTooltip, StTooltipTrigger } from "@storytell/ui/tooltip";
import {
  TbCaretDown,
  TbCaretUp,
  TbCircleCheckFilled,
  TbCoin,
  TbHelpCircle,
  TbLoader2,
  TbUserPlus,
} from "solid-icons/tb";
import { For, Show, Suspense, createSignal, onMount } from "solid-js";
import { createStore } from "solid-js/store";
import { getStripeCheckoutSuccessParam } from "~/components/billing/StripeReturnModal";
import { Logo } from "~/components/brand";
import { GradientBorder } from "~/components/prompt/internal/components";
import { useWire } from "~/wire";

export const PricingPage = (props: { inModal?: boolean }) => {
  const wire = useWire();

  // TODO: @andi Hook values from BE;
  const dailyRefill = () => 300;
  const freeCredits = () => 10000;

  const billingClient = useBillingClient();

  const [goProState, setGoProState] = createStore({
    period: "monthly" as "monthly" | "yearly",
    loading: false,
    error: null as string | null,
  });

  const [powerUserState, setPowerUserState] = createStore({
    period: "monthly" as "monthly" | "yearly",
  });

  const [refillState, setRefillState] = createStore({
    amount: 20,
    loading: false,
    error: null as string | null,
  });

  const requestCheckout = async (productType: "pro_monthly" | "pro_yearly" | "team" | "credits", quantity = 1) => {
    if (productType === "pro_monthly" || productType === "pro_yearly") {
      setGoProState("loading", true);
    }
    if (productType === "credits") {
      setRefillState("loading", true);
    }
    const successParam = getStripeCheckoutSuccessParam(productType, quantity);

    try {
      const request: CreateCheckoutRequest = {
        $typeName: "billing.v3.CreateCheckoutRequest",
        cancelUrl: `https://${APP_DOMAIN}/pricing`,
        successUrl: `https://${APP_DOMAIN}?${successParam}`,
        productType,
        quantity: productType === "credits" ? BigInt(Math.round(Math.max(10, quantity) / 10) * 10) : BigInt(1),
        metadata: {},
      };

      const result = await billingClient.createCheckout(request, useCallOptions(withBearerToken(wire.auth.token())));
      if (result.isOk()) {
        return result.value;
      }
    } catch (error) {
      if (error instanceof Error) {
        if (productType === "pro_monthly" || productType === "pro_yearly") {
          setGoProState("error", error.message);
        }
        if (productType === "credits") {
          setRefillState("error", error.message);
        }
      }
    } finally {
      setGoProState("loading", false);
      setRefillState("loading", false);
    }
  };

  const onRefill = async () => {
    stAnalytics.track("click_tracking", { cta: "pricing-refill-explore", screen: undefined });

    if (wire.auth.isGuest()) {
      wire.auth.setPostAuthAction(`refill:${refillState.amount}`);
      navigate("/auth/signup");
      return;
    }

    stAnalytics.track("pricing_refill_credits", { amount: refillState.amount * 1000 });
    const result = await requestCheckout("credits", refillState.amount * 1000);
    if (result) {
      window.location.href = result.checkoutUrl;
    }
  };

  const onSubscribePro = async () => {
    if (wire.auth.isGuest()) {
      wire.auth.setPostAuthAction(`subscribe-pro:${goProState.period}`);
      navigate("/auth/signup");
      return;
    }

    if (wire.billing.isProPlan()) {
      stAnalytics.track("click_tracking", { cta: "pricing-manage-subscription", screen: undefined });
      const client = useBillingClient();
      const res = await client.createPortalSession(
        {
          returnUrl: `https://${APP_DOMAIN}/`,
          customerId: wire.auth.user()?.id,
        },
        useCallOptions(withBearerToken(wire.auth.token())),
      );
      if (res.isOk()) {
        window.location.href = res.value.session.url;
      }
      return;
    }

    stAnalytics.track("click_tracking", { cta: "pricing-subscribe-go-pro", screen: undefined });
    const result = await requestCheckout(goProState.period === "monthly" ? "pro_monthly" : "pro_yearly");
    if (result) {
      window.location.href = result.checkoutUrl;
    }
  };

  const navigate = useNavigate();
  const location = useLocation();

  let ref!: HTMLDivElement;
  onMount(() => {
    if (ref) {
      ref.focus();
    }

    // Handle post-auth actions
    const state = location.state as { refill?: number; subscribe?: "monthly" | "yearly" } | undefined;
    if (state?.refill) {
      setRefillState("amount", state.refill);
      onRefill();
      return;
    }

    if (state?.subscribe === "monthly") {
      setGoProState("period", "monthly");
      onSubscribePro();
      return;
    }

    if (state?.subscribe === "yearly") {
      setGoProState("period", "yearly");
      onSubscribePro();
      return;
    }
  });

  return (
    <div>
      <Show when={!props.inModal && location.pathname !== "/pricing"}>
        <A href="/" class="block ml-10 mt-6">
          <Logo size={150} />
        </A>
      </Show>
      <div class="flex flex-col items-center px-3 py-10 gap-8">
        <Suspense>
          <div>
            <h2 class="text-3xl text-center">
              You have{" "}
              <span class="text-primary font-bold dark:text-blue-400">{wire.billing.credits().toLocaleString()}</span>{" "}
              credits remaining.
            </h2>
            <Show when={wire.auth.isGuest()}>
              <p class="text-secondary-text text-lg text-center mt-4">
                <A href="/auth/signup" class="text-brand underline underline-offset-2">
                  Create an account
                </A>{" "}
                and receive an extra {(10000).toLocaleString()} free credits to get you started.
              </p>
            </Show>
          </div>
        </Suspense>

        <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 xl:gap-6">
          {/* EXPLORE COLUMN */}
          <div class="relative flex flex-col max-w-xs items-center px-4 pt-10 pb-4 rounded-3xl border border-primary-border bg-tile">
            <Show when={wire.billing.isFreePlan()}>
              <div class="absolute top-4 left-5 flex items-center gap-2">
                <div class="size-2 rounded-full bg-emerald-500 animate-pulse" />
                <p class="text-sm text-secondary-text">Active Plan</p>
              </div>
            </Show>
            <h3 class="text-center text-2xl font-medium mb-3">Explore</h3>
            <p class="text-sm">Includes {freeCredits().toLocaleString()} free credits.</p>
            <p class="text-sm">Free credits refill at {dailyRefill().toLocaleString()}/day.</p>

            <div class="flex-auto flex flex-col items-center justify-center mt-10 mb-10">
              <p class="mb-3">Buy additional credits as needed</p>

              <NumberField
                class="mb-3 flex flex-col items-center"
                rawValue={refillState.amount}
                onRawValueChange={(value) => setRefillState("amount", value)}
                minValue={10}
                maxValue={100000}
                step={10}
                formatOptions={{
                  style: "currency",
                  maximumFractionDigits: 0,
                  currency: "USD",
                  roundingIncrement: 1,
                }}
                validationState={
                  refillState.amount % 10 !== 0 || refillState.amount < 10 || refillState.amount > 100000
                    ? "invalid"
                    : "valid"
                }
              >
                <div class="relative mb-2 w-40">
                  <NumberField.Input
                    ref={(el) => {
                      ref = el;
                    }}
                    class="w-40 text-xl outline-none focus:ring-2 ring-brand transition-shadow px-1 py-2 pr-4 rounded-lg border border-muted-border"
                  />

                  <div class="absolute right-0 top-0 bottom-0 flex flex-col items-center justify-between py-1 pr-1">
                    <NumberField.IncrementTrigger class="cursor-pointer rounded bg-inset hover:bg-inset-hover size-[1.1rem] grid place-content-center">
                      <TbCaretUp />
                    </NumberField.IncrementTrigger>
                    <NumberField.DecrementTrigger class="cursor-pointer rounded bg-inset hover:bg-inset-hover size-[1.1rem] grid place-content-center">
                      <TbCaretDown />
                    </NumberField.DecrementTrigger>
                  </div>
                </div>

                <NumberField.ErrorMessage>
                  <p class="text-red-500">
                    {refillState.amount < 10
                      ? "Please enter a value of at least $10"
                      : refillState.amount > 100000
                        ? "Maximum credit refill is $100,000"
                        : "Please enter a multiple of $10"}
                  </p>
                </NumberField.ErrorMessage>
              </NumberField>

              <p class="mb-3">
                for {(refillState.amount * 1000 * (wire.billing.isProPlan() ? 1.25 : 1)).toLocaleString()} credits
              </p>
              <p class="text-sm text-secondary-text">Purchased credits expire after 12 months</p>
            </div>

            <div class="flex items-center justify-end gap-4 flex-wrap w-full">
              <a
                class="inline-flex items-center gap-1 underline text-brand text-xs"
                href="https://go.storytell.ai/learn-credits"
                target="_blank"
                rel="noreferrer"
              >
                <TbHelpCircle />
                Learn how credits work
              </a>
              <button
                type="button"
                onClick={onRefill}
                disabled={
                  refillState.loading ||
                  refillState.amount < 10 ||
                  refillState.amount > 100000 ||
                  refillState.amount % 10 !== 0
                }
                class="rounded-lg cursor-pointer w-32 flex items-center justify-center gap-2 bg-brand text-on-brand px-4 py-2 disabled:opacity-70 disabled:pointer-events-none"
              >
                <Show when={refillState.loading} fallback={<TbCoin />}>
                  <TbLoader2 class="animate-spin" />
                </Show>
                Refill
              </button>
            </div>
          </div>

          {/* GO PRO COLUMN */}
          <div class="relative max-w-xs">
            <GradientBorder scaleX={2} scaleY={2} />
            <div class="flex h-full flex-col items-center px-4 pt-10 pb-4 rounded-3xl border border-primary-border bg-tile relative">
              <Show when={wire.billing.isProPlan()}>
                <div class="absolute top-4 left-5 flex items-center gap-2">
                  <div class="size-2 rounded-full bg-emerald-500 animate-pulse" />
                  <p class="text-sm text-secondary-text">Active Plan</p>
                </div>
              </Show>
              <h3 class="text-center text-2xl font-medium mb-3">Go Pro</h3>
              {/* Toggle for monthly/annual pricing */}
              <div class="relative flex items-center rounded-xl border border-muted-border overflow-hidden w-full max-w-xs">
                <div
                  class="absolute rounded-lg top-0 bottom-0 w-1/2 bg-brand transition-all duration-200"
                  style={{
                    left: goProState.period === "monthly" ? "0" : "50%",
                  }}
                />

                <button
                  type="button"
                  data-active={goProState.period === "monthly"}
                  class="w-1/2 h-10 rounded-lg bg-transparent focus:outline-none relative z-10 cursor-pointer data-[active=true]:text-on-brand"
                  onClick={() => setGoProState("period", "monthly")}
                >
                  $20/month
                </button>
                <button
                  type="button"
                  data-active={goProState.period === "yearly"}
                  class="w-1/2 h-10 rounded-lg bg-transparent focus:outline-none relative z-10 cursor-pointer data-[active=true]:text-on-brand"
                  onClick={() => setGoProState("period", "yearly")}
                >
                  $200/year
                </button>
              </div>
              <div class="flex flex-col gap-2 mt-6 text-base font-medium mb-8">
                <p>
                  Includes {goProState.period === "monthly" ? "20,000" : "240,000"} credits /{" "}
                  {goProState.period === "monthly" ? "month" : "year"}.
                </p>
              </div>
              {/* <div class="flex-auto flex flex-col items-center mt-10 mb-10">
                <p>Buy additional credits as needed.</p>
                <p class="mb-4 text-sm">25% credit bonus</p>

                <NumberField
                  class="relative mb-3"
                  rawValue={proAmount()}
                  onRawValueChange={setProAmount}
                  minValue={5}
                  formatOptions={{
                    style: "currency",
                    maximumFractionDigits: 0,
                    currency: "USD",
                    roundingIncrement: 1,
                  }}
                >
                  <NumberField.Input class="w-40 text-xl outline-none focus:ring-2 ring-brand transition-shadow px-1 py-2 pr-4 rounded-lg border border-muted-border" />

                  <div class="absolute right-0 top-0 bottom-0 flex flex-col items-center justify-between py-1 pr-1">
                    <NumberField.IncrementTrigger class="cursor-pointer rounded bg-inset hover:bg-inset-hover size-[1.1rem] grid place-content-center">
                      <TbCaretUp />
                    </NumberField.IncrementTrigger>
                    <NumberField.DecrementTrigger class="cursor-pointer rounded bg-inset hover:bg-inset-hover size-[1.1rem] grid place-content-center">
                      <TbCaretDown />
                    </NumberField.DecrementTrigger>
                  </div>
                </NumberField>

                <p class="mb-3">for {(proAmount() * 1000 * 1.25).toLocaleString()} credits</p>
              </div> */}
              <ul class="flex flex-col gap-3 mb-8 px-4 flex-auto">
                <For
                  each={[
                    "Early access to newest LLMs",
                    "Unlimited integrations",
                    "Priority support",
                    "15% credit refill bonus",
                  ]}
                >
                  {(item) => (
                    <li class="flex items-start gap-2">
                      <TbCircleCheckFilled class="text-brand mt-1" />
                      {item}
                    </li>
                  )}
                </For>
              </ul>
              <p class="mb-3 text-sm text-secondary-text max-w-xs mt-10 text-center flex-auto">
                Purchased credits don't expire for as long as you remain a subscriber.
              </p>
              <button
                type="button"
                onClick={onSubscribePro}
                disabled={goProState.loading}
                class="rounded-lg cursor-pointer flex items-center justify-center gap-2 bg-brand text-on-brand px-4 py-2 self-end disabled:opacity-70 disabled:pointer-events-none"
                classList={{
                  "w-32": !wire.billing.isProPlan(),
                  "w-60": wire.billing.isProPlan(),
                }}
              >
                <Show when={goProState.loading} fallback={<TbUserPlus />}>
                  <TbLoader2 class="animate-spin" />
                </Show>

                {wire.billing.isProPlan() ? "Manage Subscription" : "Subscribe"}
              </button>
            </div>
          </div>

          {/* POWER USER COLUMN */}
          <div class="flex flex-col max-w-xs items-center px-4 pt-8 pb-4 rounded-3xl border border-primary-border bg-tile relative">
            <h3 class="text-center text-2xl font-medium mb-3">Power User</h3>
            {/* Toggle for monthly/annual pricing */}
            <div class="relative flex items-center rounded-xl border border-muted-border overflow-hidden w-full max-w-xs">
              <div
                class="absolute rounded-lg top-0 bottom-0 w-1/2 bg-brand transition-all duration-200"
                style={{
                  left: powerUserState.period === "monthly" ? "0" : "50%",
                }}
              />

              <button
                type="button"
                data-active={powerUserState.period === "monthly"}
                class="w-1/2 h-10 rounded-lg bg-transparent focus:outline-none relative z-10 cursor-pointer data-[active=true]:text-on-brand"
                onClick={() => setPowerUserState("period", "monthly")}
              >
                $200/month
              </button>
              <button
                type="button"
                data-active={powerUserState.period === "yearly"}
                class="w-1/2 h-10 rounded-lg bg-transparent focus:outline-none relative z-10 cursor-pointer data-[active=true]:text-on-brand"
                onClick={() => setPowerUserState("period", "yearly")}
              >
                $2,000/year
              </button>
            </div>
            <div class="flex flex-col gap-2 mt-6 text-base font-medium mb-8">
              <p>
                Includes {powerUserState.period === "monthly" ? "225,000" : "2,700,000"} credits /{" "}
                {powerUserState.period === "monthly" ? "month" : "year"}.
              </p>
            </div>
            <ul class="flex flex-col gap-3 mb-8 px-4 flex-auto">
              <li class="text-sm font-medium text-secondary-text">Everything in Pro, plus:</li>
              <For
                each={[
                  "Early access to beta features",
                  "Dedicated support",
                  "CEO's personal cell number",
                  "25% credit refill bonus",
                ]}
              >
                {(item) => (
                  <li class="flex items-start gap-2">
                    <TbCircleCheckFilled class="text-brand mt-1" />
                    {item}
                  </li>
                )}
              </For>
            </ul>
            <p class="mb-3 text-sm text-secondary-text max-w-xs mt-10 text-center flex-auto">
              Purchased credits don't expire for as long as you remain a subscriber.
            </p>
            <a
              // biome-ignore lint/a11y/noBlankTarget: <explanation>
              target="_blank"
              href="mailto:sales@storytell.ai?subject=Power%20User%20Plan&body=I'd%20like%20to%20upgrade%20to%20the%20Power%20User%20plan."
              onClick={() => {
                stAnalytics.track("click_tracking", { cta: "pricing-contact-sales-power-user", screen: undefined });
              }}
              class="mb-2 block text-center w-full py-3 px-4 rounded-lg font-semibold transition-colors bg-panel hover:bg-panel-hover text-primary-text border border-primary-border"
            >
              Contact Sales
            </a>

            <span>
              Or email{" "}
              <a
                class="underline"
                onClick={() => {
                  stAnalytics.track("click_tracking", { cta: "pricing-email-sales-power-user", screen: undefined });
                }}
                href="mailto:sales@storytell.ai?subject=Power%20User%20Plan&body=I'd%20like%20to%20upgrade%20to%20the%20Power%20User%20plan."
              >
                sales@storytell.ai
              </a>
            </span>
          </div>

          {/* ENTERPRISE COLUMN */}

          <div class="flex flex-col max-w-xs items-center px-4 pt-8 pb-4 rounded-3xl border border-primary-border bg-tile relative">
            <h3 class="text-center text-2xl font-medium mb-3">Enterprise Unlimited</h3>
            <p class="text-center mb-8 text-secondary-text max-w-sm">Custom Pricing</p>

            <ul class="flex flex-col gap-3 mb-8 px-4 flex-auto">
              <For
                each={[
                  "Embed Storytell in your own applications",
                  "Per-team budgets and access controls",
                  "Custom retention policies",
                  "Report on cross-team usage",
                  "Bring your own LLM API Keys",
                  "Bring your own on-prem LLMs",
                  "Credit Card or Invoice payment",
                  "Enterprise support SLAs",
                ]}
              >
                {(item) => (
                  <li class="flex items-start gap-2">
                    <TbCircleCheckFilled class="text-brand mt-1" />
                    {item}
                  </li>
                )}
              </For>
            </ul>

            <A
              target="_blank"
              href="https://web.storytell.ai/book-a-demo"
              onClick={() => {
                stAnalytics.track("click_tracking", { cta: "pricing-contact-sales-enterprise", screen: undefined });
              }}
              class="mb-2 block text-center w-full py-3 px-4 rounded-lg font-semibold transition-colors bg-panel hover:bg-panel-hover text-primary-text border border-primary-border"
            >
              Contact Sales
            </A>

            <span>
              Or email{" "}
              <a
                class="underline"
                onClick={() => {
                  stAnalytics.track("click_tracking", { cta: "pricing-email-sales-enterprise", screen: undefined });
                }}
                href="mailto:sales@storytell.ai?subject=Enterprise%20Sales%20Request&body=Please%20share%20more%20about%20your%20interest%20in%20Storytell…"
              >
                sales@storytell.ai
              </a>
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};
